import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  ComposedChart,
} from 'recharts'

interface TrendDataPoint {
  date: string
  engagement_score: number
  reports: number
  active_users: number
}

interface EngagementTrendProps {
  data: TrendDataPoint[]
  loading?: boolean
}

export function EngagementTrend({ data, loading = false }: EngagementTrendProps) {
  const getTrendDirection = () => {
    if (data.length < 2) return 'stable'
    const recent = data.slice(-7)
    const older = data.slice(-14, -7)
    if (recent.length === 0 || older.length === 0) return 'stable'

    const recentAvg = recent.reduce((sum, d) => sum + d.engagement_score, 0) / recent.length
    const olderAvg = older.reduce((sum, d) => sum + d.engagement_score, 0) / older.length

    if (recentAvg > olderAvg * 1.05) return 'up'
    if (recentAvg < olderAvg * 0.95) return 'down'
    return 'stable'
  }

  const trend = getTrendDirection()

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number; dataKey: string }>; label?: string }) => {
    if (!active || !payload?.length) return null

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
        <p className="text-sm font-medium text-gray-900 mb-2">{label && formatDate(label)}</p>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
            <span
              className={`w-2 h-2 rounded-full ${
                entry.dataKey === 'engagement_score'
                  ? 'bg-teal-500'
                  : entry.dataKey === 'reports'
                    ? 'bg-purple-500'
                    : 'bg-blue-500'
              }`}
            />
            <span className="text-gray-500">
              {entry.dataKey === 'engagement_score'
                ? 'Engajamento'
                : entry.dataKey === 'reports'
                  ? 'Relatórios'
                  : 'Usuários'}
              :
            </span>
            <span className="font-medium text-gray-900">
              {entry.dataKey === 'engagement_score' ? `${entry.value}%` : entry.value}
            </span>
          </div>
        ))}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
        <div className="h-48 bg-gray-100 rounded animate-pulse" />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendência de Engajamento</h3>
        <div className="h-48 flex items-center justify-center text-gray-400">
          <p>Dados insuficientes para exibir tendência</p>
        </div>
      </div>
    )
  }

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'
  const trendBg = trend === 'up' ? 'bg-green-100' : trend === 'down' ? 'bg-red-100' : 'bg-gray-100'
  const trendLabel = trend === 'up' ? 'Em alta' : trend === 'down' ? 'Em queda' : 'Estável'

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Tendência de Engajamento</h3>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${trendBg}`}>
          <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          <span className={`text-sm font-medium ${trendColor}`}>{trendLabel}</span>
        </div>
      </div>

      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis yAxisId="right" orientation="right" hide />
            <Tooltip content={<CustomTooltip />} />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="engagement_score"
              stroke="#14b8a6"
              strokeWidth={2}
              fill="url(#engagementGradient)"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="reports"
              stroke="#a855f7"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-teal-500 rounded-full" />
          <span className="text-xs text-gray-500">Engajamento (%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-purple-500 rounded-full" />
          <span className="text-xs text-gray-500">Relatórios</span>
        </div>
      </div>
    </div>
  )
}
